import React, { FC, useState } from 'react';
import { DetailsRelatedTypes } from '../../utils/batchDetailsUtils';
import { BUTTON_TYPE, ButtonComponent } from '../ButtonComponent';
import { IBatchesData } from '../../interfaces/funds';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { IModalWindow, MODAL_TYPE, ModalWindow } from '../modal/ModalWindow';
import { BatchActionModalContent } from './BatchActionModalContent';
import { batchAction } from '../../store/order/actions/batches';
import { BATCHES_BASE_URL, BATCHES_TABS } from '../../routing/batchesRoutes';
import { ROUTES } from '../../routing/routesEnums';
import { getBatchDetailsPageName } from './BatchDetails';

export enum BATCH_ACTIONS {
  POST = 'post',
  UNPOST = 'unpost',
  SHELVE = 'shelve',
  UNSHELVE = 'unshelve',
}

interface Props {
  batchDetailsData: IBatchesData;
}
export const BatchDetailsInformationHeader: FC<Props> = (props) => {
  const { batchDetailsData } = props;

  const { t } = useTranslation();
  const { id: batchDetailsId } = useParams();
  const { pathname, state } = useLocation();
  const navigate = useNavigate();

  const locationPathname = getBatchDetailsPageName(pathname, batchDetailsId);

  const [modalState, setModalState] = useState<IModalWindow>({
    isOpen: false
  });

  const [isActionModalOpen, setIsActionModalOpen] = useState<boolean>(false);

  const actionModalClose = (): void => setIsActionModalOpen(false);

  const getButtonName = (): string => {
    switch (locationPathname) {
      case BATCHES_TABS.BATCHES: return t('PENDING_PAYOUTS.UNPOST_BTN_NAME');
      case BATCHES_TABS.UNPOSTED_BATCHES: return t('PENDING_PAYOUTS.POST_BTN_NAME');
      case BATCHES_TABS.SHELVED_BATCHES: return t('PENDING_PAYOUTS.UNSHELVE_BTN_NAME');
    }
  };

  const getAction = (): BATCH_ACTIONS => {
    switch (locationPathname) {
      case BATCHES_TABS.BATCHES: return BATCH_ACTIONS.UNPOST;
      case BATCHES_TABS.UNPOSTED_BATCHES: return BATCH_ACTIONS.POST;
      case BATCHES_TABS.SHELVED_BATCHES: return BATCH_ACTIONS.UNSHELVE;
    }
  };

  const getModalTitle = (): string => {
    switch (locationPathname) {
      case BATCHES_TABS.BATCHES: return t('PENDING_PAYOUTS.MODAL_TITLE_UNPOST');
      case BATCHES_TABS.UNPOSTED_BATCHES: return t('PENDING_PAYOUTS.MODAL_TITLE_POST');
      case BATCHES_TABS.SHELVED_BATCHES: return t('PENDING_PAYOUTS.UNSHELVE_MODAL_TITLE');
    }
  };

  const getModalText = (): string => {
    switch (locationPathname) {
      case BATCHES_TABS.BATCHES: return t('PENDING_PAYOUTS.MODAL_TITLE_UNPOST');
      case BATCHES_TABS.SHELVED_BATCHES: return t('PENDING_PAYOUTS.UNSHELVE_MODAL_TITLE');
    }
  };

  const handleSuccess = (action: BATCH_ACTIONS): void => {
    actionModalClose();

    let successModalText = '';
    switch (action) {
      case BATCH_ACTIONS.POST:
        successModalText = 'Batch was successfully posted.';
        break;
      case BATCH_ACTIONS.UNPOST:
        successModalText = 'Batch was successfully unposted.';
        break;
      case BATCH_ACTIONS.UNSHELVE:
        successModalText = 'Batch was successfully unshelved.';
        break;
    }

    let navigateUrl = '';

    switch (locationPathname) {
      case BATCHES_TABS.BATCHES: navigateUrl = state?.search ?? `${BATCHES_BASE_URL}${ROUTES.POSTED}`;
        break;
      case BATCHES_TABS.UNPOSTED_BATCHES: navigateUrl = state?.search ?? `${BATCHES_BASE_URL}${ROUTES.UNPOSTED}`;
        break;
      case BATCHES_TABS.SHELVED_BATCHES: navigateUrl = state?.search ?? `${BATCHES_BASE_URL}${ROUTES.SHELVED}`;
        break;
    }

    setModalState({
      isOpen: true,
      modalType: MODAL_TYPE.APPROVE,
      modalTitle: getModalTitle(),
      modalText: successModalText,
      confirmButtonText: 'Ok',
      onConfirm: async () => navigate(navigateUrl),
      onClose: async () => navigate(navigateUrl),
    });
  };

  const getButtonDisabledStatus = (): boolean => {
    switch (locationPathname) {
      case `${BATCHES_TABS.BATCHES}`: return !batchDetailsData?.canUnpost;
      case `${BATCHES_TABS.UNPOSTED_BATCHES}`: return !batchDetailsData?.canPost;
      case `${BATCHES_TABS.SHELVED_BATCHES}`: return false;
    }
  };
  
  return (
    <>
      <div className="d-flex align-items-center justify-content-between pb-5 mb-5 payout-details-separator">
        <div className="d-flex align-items-center ">
          <DetailsRelatedTypes
            sender={batchDetailsData?.sender}
            receiver={batchDetailsData?.receiver}
          />
        </div>

        <ButtonComponent
          buttonType={BUTTON_TYPE.WHITE}
          buttonName={getButtonName()}
          onButtonClick={(): void => setIsActionModalOpen(true)}
          isDisabled={getButtonDisabledStatus()}
          additionalClass="btn-sm ms-10"
        />
      </div>

      <ModalWindow
        {...modalState}
      />

      <ModalWindow
        isOpen={isActionModalOpen}
        modalType={MODAL_TYPE.SAVE}
        modalTitle={getModalTitle()}
        modalText={getModalText()}
        backdrop="static"
        content={locationPathname === BATCHES_TABS.UNPOSTED_BATCHES && <BatchActionModalContent />}
        onCancel={actionModalClose}
        onClose={actionModalClose}
        onConfirm={async (): Promise<void> => await batchAction({
          batchId: batchDetailsData?.payoutId,
          action: getAction(),
          onSuccess: handleSuccess,
          onClose: actionModalClose
        })}
      />
    </>
  );
};